import { BarChart2, Users, Shield, FileText, Calendar, ClipboardList, LogOut, ChevronLeft, ChevronRight, Sun, Moon, Settings, Languages, BellRing, Bell, ExternalLink, Radar, Clock, FileCheck, Key, UserCheck, GraduationCap, TrendingUp, Activity, Building2 } from "lucide-react";
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarSeparator, useSidebar } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/contexts/LanguageContext";
import { SlideMenu } from "@/components/common/SlideMenu";
import { SlideToLogout } from "@/components/common/SlideToLogout";
import { Switch } from "@/components/ui/switch";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useEffect, useState } from "react";
import { useUserRoles } from "@/hooks/useUserRoles";
import { cn } from "@/lib/utils";
import { useAdminCustomization } from "@/hooks/useAdminCustomization";
import { useModuleAccess } from "@/hooks/useModuleAccess";

interface MenuItem {
  title: string;
  icon: React.ComponentType<any>;
  path: string;
  external?: boolean;
  guardHide?: boolean;
  guardOnly?: boolean;
  clientHide?: boolean;
  clientOnly?: boolean;
  adminOnly?: boolean;
  adminOrClientOnly?: boolean;
  clientMode?: boolean;
  translationKey?: string;
  descriptionKey?: string;
  openInNewWindow?: boolean;
  gradient?: string;
  iconColor?: string;
  moduleName?: string;
}

const AppSidebar = () => {
  const navigate = useNavigate();
  const {
    state,
    toggleSidebar,
    setOpenMobile,
    openMobile
  } = useSidebar();
  const {
    theme,
    toggleTheme
  } = useTheme();
  const isMobile = useIsMobile();
  const {
    t,
    language,
    setLanguage
  } = useLanguage();
  const {
    user
  } = useSupabaseUser();
  const {
    hasRole,
    roles,
    isContributor
  } = useUserRoles();
  const isGuard = hasRole('guard');
  const isClient = hasRole('client');
  const isAdmin = hasRole('admin') || hasRole('super_admin');
  const isSuperAdmin = hasRole('super_admin');
  const isManager = hasRole('manager');
  
  // Get user role display
  const getUserRoleDisplay = () => {
    if (isSuperAdmin) return t('superAdmin') || 'Super Admin';
    if (isContributor) return t('contributor') || 'Contributor';
    if (isAdmin) return t('admin') || 'Admin';
    if (isManager) return t('manager') || 'Manager';
    if (isGuard) return t('guard') || 'Guard';
    if (isClient) return t('client') || 'Client';
    return t('user') || 'User';
  };
  
  const { customization } = useAdminCustomization();
  const primaryColor = customization?.primary_color || '221 83% 53%';
  const { isEnabled: isModuleEnabled } = useModuleAccess();
  const [userProfile, setUserProfile] = useState<{
    first_name?: string;
    full_name?: string;
  } | null>(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user?.id) {
        const {
          data,
          error
        } = await supabase.from('profiles').select('first_name, full_name').eq('id', user.id).single();
        if (!error && data) {
          setUserProfile(data);
        } else {
          console.error('Error fetching user profile:', error);
        }
      }
    };
    fetchUserProfile();
  }, [user]);

  const userName = userProfile?.first_name || (userProfile?.full_name ? userProfile.full_name.split(' ')[0] : '') || (user?.email ? user.email.split('@')[0] : t('user'));

  const menuItems: MenuItem[] = [
    {
      title: t('dashboard') || 'Dashboard',
      icon: BarChart2,
      path: "/dashboard",
      gradient: "from-blue-50/80 to-blue-100/60",
      iconColor: "text-blue-500/80"
    },
    {
      title: t('hrManagement') || 'HR Management',
      icon: UserCheck,
      path: "/hr",
      gradient: "from-indigo-50/80 to-indigo-100/60",
      iconColor: "text-indigo-500/80",
      adminOnly: true
    },
    {
      title: t('demographics') || 'Demographics',
      icon: TrendingUp,
      path: "/hr/demographics",
      gradient: "from-purple-50/80 to-purple-100/60",
      iconColor: "text-purple-500/80",
      adminOrClientOnly: true
    },
    {
      title: t('trainingManagement') || 'Training Management',
      icon: GraduationCap,
      path: "/training",
      gradient: "from-green-50/80 to-emerald-100/60",
      iconColor: "text-green-500/80"
    },
    {
      title: t('performanceEvaluation') || 'Performance Evaluation',
      icon: BarChart2,
      path: "/performance",
      gradient: "from-cyan-50/80 to-cyan-100/60",
      iconColor: "text-cyan-500/80"
    },
    {
      title: 'Personnel',
      icon: Shield,
      path: "/guards",
      gradient: "from-emerald-50/80 to-emerald-100/60",
      iconColor: "text-emerald-500/80"
    },
    {
      title: t('teamsMenu') || 'Teams',
      icon: Users,
      path: "/teams",
      gradient: "from-purple-50/80 to-purple-100/60",
      iconColor: "text-purple-500/80"
    },
    {
      title: t('scheduling') || 'Scheduling',
      icon: Calendar,
      path: "/scheduling",
      guardHide: true,
      clientHide: true,
      gradient: "from-indigo-50/80 to-indigo-100/60",
      iconColor: "text-indigo-500/80"
    },
    {
      title: t('My Teams Schedule') || 'My Teams Schedule',
      icon: Users,
      path: "/guard-teams",
      clientHide: true,
      guardOnly: true,
      gradient: "from-cyan-50/80 to-cyan-100/60",
      iconColor: "text-cyan-500/80"
    },
    {
      title: t('scheduleAlerts') || 'Schedule Alerts',
      icon: BellRing,
      path: "/schedule-alerts",
      guardHide: true,
      clientHide: true,
      gradient: "from-amber-50/80 to-amber-100/60",
      iconColor: "text-amber-500/80"
    },
    {
      title: t('scheduleLogs') || 'Schedule Logs',
      icon: FileCheck,
      path: "/schedule-logs",
      guardHide: true,
      clientHide: true,
      gradient: "from-violet-50/80 to-violet-100/60",
      iconColor: "text-violet-500/80"
    },
    {
      title: t('reportsMenu') || 'Reports',
      icon: FileText,
      path: "/reports",
      gradient: "from-orange-50/80 to-orange-100/60",
      iconColor: "text-orange-500/80"
    },
    {
      title: t('incidentAlerts') || 'Incident Alerts',
      icon: Bell,
      path: "/alerts",
      adminOnly: true,
      clientHide: true,
      guardHide: true,
      translationKey: "incidentAlerts",
      descriptionKey: "incidentAlertsDesc",
      gradient: "from-red-50/80 to-red-100/60",
      iconColor: "text-red-500/80"
    },
    {
      title: t('workInstructions') || 'Work Instructions',
      icon: ClipboardList,
      path: "/missions",
      gradient: "from-teal-50/80 to-teal-100/60",
      iconColor: "text-teal-500/80"
    },
    {
      title: t('settings') || 'Settings',
      icon: Settings,
      path: "/settings",
      adminOnly: true,
      gradient: "from-gray-50/80 to-gray-100/60",
      iconColor: "text-gray-500/80"
    },
    {
      title: t('userCredentials') || 'User Credentials',
      icon: Key,
      path: "/user-credentials",
      adminOnly: true,
      gradient: "from-yellow-50/80 to-yellow-100/60",
      iconColor: "text-yellow-600/80"
    },
    {
      title: t('documentation') || 'Documentation',
      icon: FileText,
      path: "/documentation",
      gradient: "from-blue-50/80 to-indigo-100/60",
      iconColor: "text-blue-500/80"
    }
  ];

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const handleMenuItemClick = (path: string, event?: React.MouseEvent, isExternal?: boolean, openInNewWindow?: boolean) => {
    if (event) {
      event.preventDefault();
    }
    if (isExternal) {
      if (openInNewWindow) {
        window.open(path, '_blank');
      } else {
        window.location.href = path;
      }
    } else {
      navigate(path);
      if (isMobile && openMobile) {
        setOpenMobile(false);
      }
    }
  };

  const toggleLanguage = () => {
    const langs = ['en', 'el', 'fr'];
    const currentIndex = langs.indexOf(language);
    const nextIndex = (currentIndex + 1) % langs.length;
    setLanguage(langs[nextIndex]);
  };

  const filteredMenuItems = menuItems.filter(item => {
    // Role-based filtering
    if (item.guardHide && isGuard) return false;
    if (item.guardOnly && !isGuard) return false;
    if (item.clientHide && isClient) return false;
    if (item.adminOnly && !isAdmin) return false;
    if (item.adminOrClientOnly && !(isAdmin || isClient)) return false;
    
    // Module access filtering
    const featureName = item.moduleName || 
                       (item.external ? item.title.toLowerCase().replace(/\s+/g, '_') : 
                       item.path.replace(/^\//, '').split('/')[0]);
    if (!isModuleEnabled(featureName)) return false;
    
    return true;
  });

  return (
    <Sidebar>
      <SidebarContent className="flex flex-col h-full bg-gradient-to-br from-background/95 via-background to-muted/20 backdrop-blur-xl border-r border-border/50 pt-16">
        {/* Header */}
        <div className="p-4 mx-3 mt-3 mb-2">
          <div className="flex items-center gap-2 bg-gradient-to-r from-primary/5 via-secondary/5 to-accent/5 backdrop-blur-sm rounded-2xl p-4 border border-border/30 shadow-sm hover:shadow-md transition-all duration-300">
            <div className="flex items-center gap-3">
              <div className="relative w-9 h-9 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center shadow-sm border border-border/40 overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                {customization?.logo_url ? (
                  <img 
                    src={customization.logo_url} 
                    alt="Company Logo" 
                    className="h-7 w-7 object-contain opacity-90 relative z-10" 
                  />
                ) : (
                  <Building2 className="h-5 w-5 text-primary opacity-80 relative z-10" />
                )}
              </div>
              <h1 className="text-xl font-bold transition-all duration-300" style={{ color: `hsl(${primaryColor})`, opacity: state === "collapsed" ? 0 : 1, width: state === "collapsed" ? 0 : 'auto' }}>
                {customization?.app_name || 'Shiftask'}
              </h1>
            </div>
          </div>
        </div>
        
        {/* Welcome Section */}
        <div className={`mx-3 mb-4 transition-all duration-300 ${state === "collapsed" ? "opacity-0 h-0 overflow-hidden mx-0 mb-0" : "opacity-100"}`}>
          <div className="relative bg-gradient-to-r from-primary/5 via-secondary/5 to-accent/5 backdrop-blur-sm rounded-2xl p-4 border border-border/30 shadow-sm overflow-hidden group">
            {/* Animated background */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary/2 to-secondary/2 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            <div className="relative flex items-center gap-3 mb-2">
              <div className="w-2 h-2 bg-gradient-to-r from-primary to-secondary rounded-full animate-pulse"></div>
              <h2 className="font-semibold text-sm text-foreground/90">
                {t('welcome')}, <span className="font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">{userName}</span>
              </h2>
            </div>
            <p className="relative text-xs flex items-center gap-2" style={{ color: `hsl(${primaryColor})`, opacity: 0.8 }}>
              <Shield className="h-3 w-3" />
              <span className="font-medium">{getUserRoleDisplay()}</span>
            </p>
          </div>
        </div>
        
        <SidebarSeparator className="mx-4 bg-border/30" />
          
        {/* Navigation Menu */}
        <SidebarGroup className="px-3 flex-1">
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1.5">
              {filteredMenuItems.map((item, index) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild size="lg">
                    <a 
                      href={item.path} 
                      onClick={e => handleMenuItemClick(item.path, e, item.external, item.openInNewWindow)} 
                      className="group relative overflow-visible flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 hover:scale-[1.01] text-sm border bg-gradient-to-r from-background/60 to-muted/30 hover:from-primary/5 hover:to-secondary/5 border-border/20 hover:border-border/40 hover:shadow-sm"
                      target={item.openInNewWindow ? "_blank" : undefined} 
                      rel={item.openInNewWindow ? "noopener noreferrer" : undefined}
                      style={{
                        animationDelay: `${index * 20}ms`
                      }}
                    >
                      {/* Subtle hover effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-primary/3 to-secondary/3 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                      
                      <div className={cn(
                        "relative flex-shrink-0 rounded-full flex items-center justify-center transition-all duration-200 group-hover:scale-105 bg-muted/40 group-hover:bg-primary/10 border border-border/30 group-hover:border-primary/20",
                        state === "collapsed" ? "w-7 h-7" : "w-9 h-9"
                      )}>
                        <item.icon className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors duration-200" />
                      </div>
                      
                      <span className="relative font-medium text-foreground/80 group-hover:text-foreground transition-colors duration-200">
                        {item.title}
                      </span>
                      
                      {item.external && item.openInNewWindow && (
                        <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <div className="w-3 h-3 border border-muted-foreground/40 rounded-sm flex items-center justify-center bg-background/60">
                            <div className="w-0.5 h-0.5 border-t border-r border-muted-foreground rotate-45"></div>
                          </div>
                        </div>
                      )}
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        
        <SidebarSeparator className="mt-4 mb-4 mx-4 bg-border/30" />
        
        {/* Language Toggle */}
        <div className="px-3 py-2">
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-3 bg-gradient-to-r from-primary/5 to-secondary/5 backdrop-blur-sm rounded-xl p-3 border border-border/30 shadow-sm">
              <span className={`text-sm font-medium transition-colors ${language === "el" ? "text-primary" : "text-muted-foreground"}`}>
                ΕΛ
              </span>
              <Switch 
                checked={language === "en"} 
                onCheckedChange={toggleLanguage} 
                aria-label="Toggle language"
                className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted"
              />
              <span className={`text-sm font-medium transition-colors ${language === "en" ? "text-primary" : "text-muted-foreground"}`}>
                EN
              </span>
            </div>
          </div>
        </div>
        
        <SidebarSeparator className="mt-2 mb-4 mx-4 bg-border/30" />
          
        {/* Action Buttons */}
        <div className="mt-auto p-3 space-y-2">
          <Button 
            variant="ghost" 
            className="w-full flex items-center justify-start gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 hover:scale-[1.01] text-sm border bg-gradient-to-r from-background/60 to-muted/30 hover:from-warning/5 hover:to-warning/10 border-border/20 hover:border-warning/20 hover:shadow-sm group relative overflow-hidden" 
            onClick={toggleTheme}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-warning/3 to-warning/5 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            <div className="relative flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 group-hover:scale-105 bg-muted/40 group-hover:bg-warning/10 border border-border/30 group-hover:border-warning/20">
                {theme === "light" ? (
                  <Moon className="w-4 h-4 text-muted-foreground group-hover:text-warning transition-colors duration-200" />
                ) : (
                  <Sun className="w-4 h-4 text-muted-foreground group-hover:text-warning transition-colors duration-200" />
                )}
              </div>
              <span className="font-medium text-foreground/80 group-hover:text-foreground transition-colors duration-200">
                {theme === "light" ? t("darkMode") : t("lightMode")}
              </span>
            </div>
          </Button>
          
          <SlideToLogout onLogout={handleLogout} />
        </div>
      </SidebarContent>
      
      <SidebarFooter className="bg-gradient-to-r from-secondary/5 to-accent/5 backdrop-blur-sm border-t border-border/30">
        <div className="flex items-center px-4 py-3">
          <div className={`flex items-center gap-3 transition-opacity duration-300 ${state === "collapsed" ? "opacity-0" : "opacity-100"}`}>
            <div className="w-6 h-6 bg-gradient-to-br from-secondary/10 to-accent/10 rounded-lg flex items-center justify-center shadow-sm border border-border/30">
              <img src="/lovable-uploads/a28d2ad7-ee8f-4f8e-a49c-83b00763d61c.png" alt="OVIT Logo" className="h-4 w-auto opacity-80" />
            </div>
            <span className="text-xs font-medium text-muted-foreground">
              Created by OVIT
            </span>
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
